import { DEFAULT_DRUMKIT_PARAMS } from "../audio/instruments/drumkit";
import { DEFAULT_SAMPLER_PARAMS } from "../audio/instruments/sampler";
import { DEFAULT_SYNTH_PARAMS } from "../audio/instruments/synth";
import { DEFAULT_DELAY_PARAMS } from "../audio/effects/delay";
import { DEFAULT_REVERB_PARAMS } from "../audio/effects/reverb";
import { DEFAULT_STRIP } from "../audio/mixer";
import { createId } from "../core/ids";
import { emptyDrumPattern } from "../sequencer/patterns";
import { CURRENT_PROJECT_VERSION, PROJECT_FORMAT, type Project } from "./schema";

/** A new project with a basic beat and a I–V–vi–IV progression so it plays immediately. */
export function createDefaultProject(name = "Untitled project", now = new Date()): Project {
  const keys = createId(), drums = createId(), chords = createId(), pads = createId();
  const pattern = emptyDrumPattern(16);
  [0, 8].forEach((s) => (pattern.lanes.kick[s] = true));
  [4, 12].forEach((s) => (pattern.lanes.snare[s] = true));
  [0, 2, 4, 6, 8, 10, 12, 14].forEach((s) => (pattern.lanes.hat[s] = true));
  const iso = now.toISOString();
  return {
    format: PROJECT_FORMAT,
    schemaVersion: CURRENT_PROJECT_VERSION,
    id: createId(),
    name,
    createdAt: iso,
    updatedAt: iso,
    revision: 0,
    song: {
      title: "", artist: "", notes: "", references: [], key: { tonic: "C", mode: "major" }, transposeSemitones: 0, transposeLiveInput: false,
      tempoBpm: 100, timeSignature: { beatsPerBar: 4, beatUnit: 4 },
    },
    tracks: [
      { id: keys, name: "Keys", instrument: { kind: "synth", params: { ...DEFAULT_SYNTH_PARAMS } }, mixer: { ...DEFAULT_STRIP } },
      { id: chords, name: "Chords", instrument: { kind: "synth", params: { ...DEFAULT_SYNTH_PARAMS, waveform: "triangle", osc2Waveform: "sawtooth", osc2Mix: 0.2, attackSec: 0.05, releaseSec: 0.6, cutoffHz: 1800, volume: 0.5 } }, mixer: { ...DEFAULT_STRIP, volume: 0.6, sendReverb: 0.3 } },
      { id: drums, name: "Drums", instrument: { kind: "drumkit", params: { ...DEFAULT_DRUMKIT_PARAMS } }, mixer: { ...DEFAULT_STRIP, sendReverb: 0.05 } },
      { id: pads, name: "Sample", instrument: { kind: "sampler", params: { ...DEFAULT_SAMPLER_PARAMS, mode: "oneshot" }, assetId: null }, mixer: { ...DEFAULT_STRIP } },
    ],
    sequence: {
      drums: { trackId: drums, pattern },
      progression: { trackId: chords, chords: [1, 5, 6, 4].map((d) => ({ degree: d as 1 | 5 | 6 | 4, seventh: false })), octave: 3, inversion: 0, rhythm: "whole", bassNote: true, enabled: true },
    },
    effects: { reverb: { ...DEFAULT_REVERB_PARAMS }, delay: { ...DEFAULT_DELAY_PARAMS } },
    master: { volume: 0.8 },
    metronome: { enabled: false, volume: 0.6, countInBars: 0 },
    playground: { keyboardTrackId: keys, padsTrackId: pads, octave: 4 },
    extensions: {},
  };
}
